import { createElement, type ReactElement, type ReactNode } from "react";
import type { ComponentRegistry } from "./registry";
import type { SduiNode } from "./types";

/**
 * Walk a parsed SDUI tree and render it as a React element by looking up each
 * node's `type` in the supplied {@link ComponentRegistry}.
 *
 * The interpreter is purely structural:
 *
 * - It owns no UI styling, layout, or data fetching.
 * - It never reads `node.props` — props are forwarded opaquely to the host
 *   component, which owns interpretation per primitive.
 * - It does not evaluate `node.bindings` — reactive rules are handled in a
 *   separate task (E2.S2). For v1 the interpreter passes through the static
 *   tree only.
 *
 * Each child is given a stable React `key` derived from its position so that
 * React's reconciler can identify list items across renders. The key is a
 * sibling-local index; the registry consumer is responsible for opting into a
 * stable identity if it has a domain-meaningful `props.key`.
 *
 * @throws Error when `node.type` is not present in the registry. This is the
 * fail-loud behaviour required by the closed v1 vocabulary — unknown
 * primitives must not silently degrade.
 */
export function interpret(node: SduiNode, registry: ComponentRegistry): ReactElement
{
    const Component = registry[node.type];
    if (!Component)
    {
        throw new Error(`Unknown SDUI primitive: ${String(node.type)}`);
    }

    const children: ReactNode[] | undefined = node.children?.map(
        (child, index) => interpretChild(child, registry, index),
    );

    return createElement(Component, { props: node.props }, children);
}

function interpretChild(node: SduiNode, registry: ComponentRegistry, index: number): ReactElement
{
    const Component = registry[node.type];
    if (!Component)
    {
        throw new Error(`Unknown SDUI primitive: ${String(node.type)}`);
    }

    const children: ReactNode[] | undefined = node.children?.map(
        (child, childIndex) => interpretChild(child, registry, childIndex),
    );

    return createElement(Component, { props: node.props, key: index }, children);
}
